import { useEffect, useMemo, useState } from "react";

import { api } from "@/api/client";
import { useStore } from "@/state/store";
import { Icon } from "@/components/atoms/Icons";
import type { components } from "@/api/schema.gen";

type MarketView = components["schemas"]["MarketView"];

export function SearchOverlay() {
  const open = useStore((s) => s.searchOpen);
  const setOpen = useStore((s) => s.setSearchOpen);
  const openDetail = useStore((s) => s.openDetail);
  const [q, setQ] = useState("");
  const [all, setAll] = useState<MarketView[]>([]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const load = async () => {
      const { data } = await api.GET("/v1/markets", {
        params: { query: { limit: 50 } },
      });
      if (cancelled || !data) return;
      setAll(data.items);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, setOpen]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const items = needle
      ? all.filter(
          (m) =>
            m.title.toLowerCase().includes(needle) ||
            m.description.toLowerCase().includes(needle) ||
            m.category.toLowerCase().includes(needle),
        )
      : all;
    return items.slice(0, 8);
  }, [q, all]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 p-6 pt-[12vh] backdrop-blur-md"
      onClick={(e) => {
        if (e.target === e.currentTarget) setOpen(false);
      }}
    >
      <div className="w-[640px] max-w-[92vw] overflow-hidden rounded-modal border border-line bg-bg-1 shadow-[var(--shadow-card)]">
        <div className="flex items-center gap-2 border-b border-line px-4 py-3">
          <Icon.Search size={16} className="text-muted" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search markets…"
            className="flex-1 bg-transparent text-[14px] outline-none placeholder:text-muted"
          />
          <span className="rounded border border-line-strong bg-bg-3 px-2 py-[2px] text-[10px] text-muted">
            ESC
          </span>
        </div>
        <ul className="max-h-[60vh] overflow-y-auto">
          {filtered.length === 0 ? (
            <li className="px-4 py-8 text-center text-[13px] text-muted">
              No markets match “{q}”.
            </li>
          ) : (
            filtered.map((m) => (
              <li key={m.id}>
                <button
                  className="flex w-full items-start gap-3 border-b border-line px-4 py-3 text-left transition-colors hover:bg-bg-2"
                  onClick={() => {
                    openDetail(m.slug);
                    setOpen(false);
                  }}
                >
                  <span className="mt-1 h-2 w-2 rounded-full bg-accent" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[13.5px]">{m.title}</div>
                    <div className="mt-1 truncate text-[11.5px] text-muted">
                      {m.category} · resolves{" "}
                      {new Date(m.resolve_after).toLocaleDateString()}
                    </div>
                  </div>
                  <Icon.ChevronRight size={14} className="mt-2 text-muted" />
                </button>
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
  );
}
